import React, { useEffect, useState } from "react";
import { api, formatErr, fmtMoney, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Download, FileType, TrendingUp, TrendingDown, Package, Award, DollarSign, AlertTriangle, CalendarClock, Wallet, Receipt } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useFeature } from "@/contexts/FeaturesContext";
import { useI18n } from "@/contexts/I18nContext";
import { useSearchParams } from "react-router-dom";
import { currencyName } from "@/lib/currencies";

export default function Reports() {
  const { currentCompany } = useAuth();
  const advancedReportsEnabled = useFeature("advanced_reports");
  const { t } = useI18n();
  const ti = (key, vars = {}) => Object.entries(vars).reduce((s, [k, v]) => s.replaceAll(`{${k}}`, v), t(key));
  const cur = currentCompany?.base_currency || "SYP";
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "sales";
  const [tab, setTab] = useState(initialTab);
  useEffect(() => {
    const q = searchParams.get("tab");
    if (q && q !== tab) setTab(q);
    // eslint-disable-next-line
  }, [searchParams]);
  const onTabChange = (v) => {
    setTab(v);
    const sp = new URLSearchParams(searchParams);
    sp.set("tab", v);
    setSearchParams(sp, { replace: true });
  };
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [rateMode, setRateMode] = useState("invoice");
  const [sales, setSales] = useState(null);
  const [purchases, setPurchases] = useState(null);
  const [inv, setInv] = useState(null);
  const [profit, setProfit] = useState(null);
  const [top, setTop] = useState(null);
  const [lowStock, setLowStock] = useState([]);
  const [expiring, setExpiring] = useState([]);
  const [pnl, setPnl] = useState(null);
  const [vat, setVat] = useState(null);
  const [zReport, setZReport] = useState(null);
  const [zLoading, setZLoading] = useState(false);
  const [category, setCategory] = useState("");
  const [categories, setCategories] = useState([]);
  useEffect(() => { api.get("/categories").then(r => setCategories(r.data)).catch(()=>{}); }, []);

  const loadZReport = async () => {
    setZLoading(true);
    try {
      const params = {};
      if (from) params.from_date = from;
      if (to) params.to_date = to;
      const { data } = await api.get("/reports/z-report", { params });
      setZReport(data);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setZLoading(false); }
  };

  const printZ = () => {
    if (!zReport) return;
    const w = window.open("", "_blank", "width=400,height=600");
    const cashiers = zReport.by_cashier.map(c => `<tr><td>${c.name}</td><td>${c.count}</td><td class="tn">${c.total.toLocaleString("ar-EG")}</td><td class="tn">${c.cash.toLocaleString("ar-EG")}</td><td class="tn">${c.credit.toLocaleString("ar-EG")}</td></tr>`).join("");
    const byCur = (zReport.by_currency || []).map(c => `
      <div style="margin-top:6px;font-weight:bold;border-bottom:1px solid #000">${currencyName(c.currency)} (${c.currency})</div>
      <div class="row"><span>نقدي</span><span class="tn">${c.cash_total.toLocaleString("ar-EG")}</span></div>
      <div class="row"><span>آجل</span><span class="tn">${c.credit_total.toLocaleString("ar-EG")}</span></div>
      <div class="row"><span>مرتجعات</span><span class="tn">- ${c.returns_total.toLocaleString("ar-EG")}</span></div>
      <div class="row" style="font-weight:bold"><span>صافي</span><span class="tn">${c.net_cash.toLocaleString("ar-EG")} ${c.currency}</span></div>`).join("");
    w.document.write(`
      <html dir="rtl"><head><title>Z-Report</title>
      <style>@page{size:80mm auto;margin:4mm}body{font-family:Cairo,Arial;width:72mm;margin:0 auto;font-size:12px}
      h1{text-align:center;font-size:14px;margin:4px 0}
      .row{display:flex;justify-content:space-between;padding:2px 0;border-bottom:1px dashed #999}
      table{width:100%;font-size:10px;margin-top:6px;border-collapse:collapse}
      th,td{padding:2px;border-bottom:1px solid #ddd;text-align:start}
      .tn{font-variant-numeric:tabular-nums}
      .total{font-weight:bold;font-size:14px;border-top:2px solid #000;padding-top:4px;margin-top:6px}
      </style></head><body>
      <h1>${currentCompany?.name || "الشركة"}</h1>
      ${currentCompany?.vat_number ? `<div style="text-align:center;font-size:10px">الرقم الضريبي: ${currentCompany.vat_number}</div>` : ""}
      <div style="text-align:center;font-weight:bold">تقرير نهاية اليوم (Z-Report)</div>
      <div style="text-align:center;font-size:10px">${new Date(zReport.from_date).toLocaleDateString("ar-EG")} → ${new Date(zReport.to_date).toLocaleDateString("ar-EG")}</div>
      <div class="row"><span>عدد فواتير البيع</span><span class="tn">${zReport.sales_count}</span></div>
      <div class="row"><span>عدد المرتجعات</span><span class="tn">${zReport.returns_count}</span></div>
      ${byCur ? `<div style="margin-top:6px;font-weight:bold;text-align:center">التفصيل حسب العملة</div>${byCur}` : ""}
      <div class="row total"><span>صافي النقدية${zReport.multi_currency ? ` (بـ${zReport.base_currency})` : ""}</span><span class="tn">${zReport.net_cash.toLocaleString("ar-EG")} ${zReport.base_currency || cur}</span></div>
      ${zReport.by_cashier.length ? `<div style="margin-top:8px;font-weight:bold">حسب الكاشير:</div><table><thead><tr><th>الاسم</th><th>عدد</th><th>الإجمالي</th><th>نقدي</th><th>آجل</th></tr></thead><tbody>${cashiers}</tbody></table>` : ""}
      <div style="text-align:center;margin-top:8px;font-size:10px">${new Date().toLocaleString("ar-EG")}</div>
      <script>window.onload=()=>setTimeout(()=>window.print(),200)</script>
      </body></html>
    `);
  };

  const printVat = () => {
    if (!vat) return;
    const w = window.open("", "_blank", "width=800,height=600");
    const bc = vat.base_currency || cur;
    const period = `${vat.from_date || "البداية"} → ${vat.to_date || "الآن"}`;
    w.document.write(`
      <html dir="rtl"><head><title>الإقرار الضريبي</title>
      <style>@page{size:A4;margin:14mm}body{font-family:Cairo,Arial;font-size:13px;color:#111}
      h1{font-size:18px;margin:0 0 2px}.sub{color:#555;font-size:11px}
      table{width:100%;border-collapse:collapse;margin-top:16px}
      th,td{padding:8px 10px;border:1px solid #d1d5db;text-align:start}
      th{background:#f1f5f9}.tn{font-variant-numeric:tabular-nums;text-align:end}
      .net{background:#0f766e;color:#fff;font-weight:bold;font-size:15px}
      .head{border-bottom:3px solid #0f766e;padding-bottom:8px;margin-bottom:8px}
      </style></head><body>
      <div class="head">
        <h1>${currentCompany?.name || "الشركة"}</h1>
        ${currentCompany?.vat_number ? `<div class="sub">الرقم الضريبي: <strong>${currentCompany.vat_number}</strong></div>` : ""}
        <div class="sub">تقرير ضريبة القيمة المضافة (VAT) · الفترة: ${period}</div>
      </div>
      <table>
        <thead><tr><th>البند</th><th class="tn">الوعاء الخاضع</th><th class="tn">قيمة الضريبة (${bc})</th></tr></thead>
        <tbody>
          <tr><td>ضريبة المخرجات (مبيعات) — ${vat.sale_invoices} فاتورة</td><td class="tn">${vat.output_taxable.toLocaleString("ar-EG")}</td><td class="tn">${vat.output_vat.toLocaleString("ar-EG")}</td></tr>
          <tr><td>ضريبة المدخلات (مشتريات) — ${vat.purchase_invoices} فاتورة</td><td class="tn">${vat.input_taxable.toLocaleString("ar-EG")}</td><td class="tn">${vat.input_vat.toLocaleString("ar-EG")}</td></tr>
          <tr class="net"><td colspan="2">صافي الضريبة المستحقة للدفع</td><td class="tn">${vat.net_vat_payable.toLocaleString("ar-EG")} ${bc}</td></tr>
        </tbody>
      </table>
      <p class="sub" style="margin-top:14px">صافي الضريبة = ضريبة المخرجات − ضريبة المدخلات. القيمة الموجبة تعني مبلغاً مستحقاً لمصلحة الضرائب.</p>
      <div class="sub" style="margin-top:20px">طُبع في: ${new Date().toLocaleString("ar-EG")}</div>
      <script>window.onload=()=>setTimeout(()=>window.print(),200)</script>
      </body></html>
    `);
  };


  const load = async () => {
    try {
      const params = {};
      if (from) params.from_date = from;
      if (to) params.to_date = to;
      if (category) params.category = category;
      params.rate_mode = rateMode;
      const [s, p, i, pr, t, ls, ex, pl, vt] = await Promise.all([
        api.get("/reports/sales", { params }),
        api.get("/reports/purchases", { params }),
        api.get("/reports/inventory", { params: category ? { category } : {} }),
        advancedReportsEnabled ? api.get("/reports/profit", { params }) : Promise.resolve({ data: null }),
        advancedReportsEnabled ? api.get("/reports/top-selling") : Promise.resolve({ data: null }),
        api.get("/products", { params: { low_stock: true } }),
        api.get("/products", { params: { expiring_days: 60 } }),
        advancedReportsEnabled ? api.get("/reports/pnl", { params }) : Promise.resolve({ data: null }),
        api.get("/reports/vat", { params }),
      ]);
      setSales(s.data); setPurchases(p.data); setInv(i.data); setProfit(pr.data); setTop(t.data);
      setLowStock(ls.data); setExpiring(ex.data); setPnl(pl.data); setVat(vt.data);
    } catch(e){ toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const exportFile = (report, format = "excel") => {
    const cid = localStorage.getItem("company_id");
    const token = localStorage.getItem("access_token");
    const params = new URLSearchParams();
    if (from) params.set("from_date", from);
    if (to) params.set("to_date", to);
    params.set("format", format);
    const url = `${API}/reports/export/${report}?${params.toString()}`;
    fetch(url, { headers: { "X-Company-Id": cid, "Authorization": `Bearer ${token}` }, credentials: "include" })
      .then(r => r.blob())
      .then(b => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(b);
        a.download = `${report}_report.${format === "pdf" ? "pdf" : "xlsx"}`;
        a.click();
      })
      .catch(() => toast.error(t("rep_export_failed")));
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold">{t("reports")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t("rep_subtitle")}</p>
          {currentCompany?.vat_number && <p className="text-xs text-muted-foreground mt-0.5" data-testid="reports-vat">{t("rep_vat_number")} <span className="font-semibold text-foreground">{currentCompany.vat_number}</span></p>}
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <div><Label className="text-xs">{t("rep_from")}</Label><Input type="date" value={from} onChange={(e)=>setFrom(e.target.value)} className="h-9" /></div>
          <div><Label className="text-xs">{t("rep_to")}</Label><Input type="date" value={to} onChange={(e)=>setTo(e.target.value)} className="h-9" /></div>
          <div>
            <Label className="text-xs">{t("rep_group")}</Label>
            <select value={category} onChange={(e)=>setCategory(e.target.value)} className="h-9 px-2 border border-border rounded-md bg-background text-sm" data-testid="report-cat-filter">
              <option value="">{t("rep_all_groups")}</option>
              {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs">{t("rep_rate_mode")}</Label>
            <select value={rateMode} onChange={(e)=>setRateMode(e.target.value)} className="h-9 px-2 border border-border rounded-md bg-background text-sm" data-testid="report-rate-mode">
              <option value="invoice">{t("rep_rate_invoice")}</option>
              <option value="current">{t("rep_rate_current")}</option>
            </select>
          </div>
          <Button onClick={load} className="h-9">{t("refresh")}</Button>
        </div>
      </div>

      {/* Z-Report / End of day */}
      <Card className="p-5 border-primary/30 bg-primary/5" data-testid="z-report-card">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2 className="font-heading font-bold text-lg flex items-center gap-2">{t("rep_z_title")}</h2>
            <p className="text-xs text-muted-foreground mt-1">{t("rep_z_desc")}</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={loadZReport} disabled={zLoading} variant="outline" className="h-9" data-testid="z-report-load">{zLoading ? t("rep_calculating") : t("rep_calc")}</Button>
            {zReport && <Button onClick={printZ} className="h-9" data-testid="z-report-print">{t("rep_print_80")}</Button>}
          </div>
        </div>
        {zReport && (
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div className="p-3 bg-background rounded-md border border-border"><div className="text-xs text-muted-foreground">{t("rep_sales_count")}</div><div className="text-xl font-bold tabular-nums mt-1">{zReport.sales_count}</div></div>
            <div className="p-3 bg-emerald-50 rounded-md border border-emerald-200"><div className="text-xs text-emerald-700">{t("rep_cash")}</div><div className="text-xl font-bold tabular-nums mt-1 text-emerald-800">{fmtMoney(zReport.cash_total, cur)}</div></div>
            <div className="p-3 bg-amber-50 rounded-md border border-amber-200"><div className="text-xs text-amber-700">{t("rep_credit")}</div><div className="text-xl font-bold tabular-nums mt-1 text-amber-800">{fmtMoney(zReport.credit_total, cur)}</div></div>
            <div className="p-3 bg-red-50 rounded-md border border-red-200"><div className="text-xs text-red-700">{t("rep_returns")} ({zReport.returns_count})</div><div className="text-xl font-bold tabular-nums mt-1 text-red-800">- {fmtMoney(zReport.returns_total, cur)}</div></div>
            <div className="col-span-2 md:col-span-4 p-3 bg-primary text-primary-foreground rounded-md"><div className="text-xs opacity-80">{t("rep_net_cash_box")}{zReport.multi_currency ? ` ${ti("rep_converted_to", { c: zReport.base_currency })}` : ""}</div><div className="text-2xl font-bold tabular-nums mt-1">{fmtMoney(zReport.net_cash, zReport.base_currency || cur)}</div></div>
            {Array.isArray(zReport.by_currency) && zReport.by_currency.length > 0 && (
              <div className="col-span-2 md:col-span-4 space-y-3" data-testid="z-report-by-currency">
                <div className="text-xs font-bold">{t("rep_by_currency")}</div>
                <div className="grid md:grid-cols-2 gap-4">
                  {zReport.by_currency.map((c) => (
                    <div key={c.currency} className="rounded-md border border-border overflow-hidden bg-background" data-testid={`z-currency-${c.currency}`}>
                      <div className="bg-muted px-3 py-2 font-bold text-sm flex items-center justify-between">
                        <span>{currencyName(c.currency)} ({c.currency})</span>
                        <span className="text-xs text-muted-foreground font-normal">{ti("rep_sales_returns_count", { s: c.sales_count, r: c.returns_count })}</span>
                      </div>
                      <table className="w-full text-sm">
                        <tbody>
                          <tr className="border-t border-border"><td className="p-2 text-muted-foreground">{t("rep_cash")}</td><td className="p-2 text-end tabular-nums text-emerald-700 font-bold">{fmtMoney(c.cash_total, c.currency)}</td></tr>
                          <tr className="border-t border-border"><td className="p-2 text-muted-foreground">{t("rep_credit")}</td><td className="p-2 text-end tabular-nums text-amber-700 font-bold">{fmtMoney(c.credit_total, c.currency)}</td></tr>
                          <tr className="border-t border-border"><td className="p-2 text-muted-foreground">{t("rep_returns")}</td><td className="p-2 text-end tabular-nums text-red-700 font-bold">- {fmtMoney(c.returns_total, c.currency)}</td></tr>
                          <tr className="border-t border-border bg-primary/5"><td className="p-2 font-bold">{t("rep_net_cash")}</td><td className="p-2 text-end tabular-nums font-bold text-primary">{fmtMoney(c.net_cash, c.currency)}</td></tr>
                        </tbody>
                      </table>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {zReport.by_cashier.length > 0 && (
              <div className="col-span-2 md:col-span-4">
                <div className="text-xs font-bold mb-2">{t("rep_cashier_detail")}</div>
                <Table>
                  <TableHeader><TableRow><TableHead>{t("rep_cashier")}</TableHead><TableHead>{t("rep_count")}</TableHead><TableHead>{t("total")}</TableHead><TableHead>{t("rep_cash")}</TableHead><TableHead>{t("rep_credit")}</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {zReport.by_cashier.map((c, i) => (
                      <TableRow key={i}><TableCell className="font-bold">{c.name}</TableCell><TableCell className="tabular-nums">{c.count}</TableCell><TableCell className="tabular-nums font-bold">{fmtMoney(c.total, cur)}</TableCell><TableCell className="tabular-nums text-emerald-600">{fmtMoney(c.cash, cur)}</TableCell><TableCell className="tabular-nums text-amber-600">{fmtMoney(c.credit, cur)}</TableCell></TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        )}
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4"><div className="flex items-center justify-between"><span className="text-xs uppercase text-muted-foreground">{t("rep_sales")}</span><TrendingUp className="w-4 h-4 text-emerald-600" /></div><div className="mt-2 text-xl font-heading font-bold tabular-nums">{fmtMoney(sales?.total_in_base ?? sales?.total, sales?.base_currency || cur)}</div><div className="text-xs text-muted-foreground">{sales?.count||0} {t("rep_invoice_word")}</div></Card>
        <Card className="p-4"><div className="flex items-center justify-between"><span className="text-xs uppercase text-muted-foreground">{t("rep_purchases")}</span><TrendingDown className="w-4 h-4" /></div><div className="mt-2 text-xl font-heading font-bold tabular-nums">{fmtMoney(purchases?.total_in_base ?? purchases?.total, purchases?.base_currency || cur)}</div><div className="text-xs text-muted-foreground">{purchases?.count||0} {t("rep_invoice_word")}</div></Card>
        {advancedReportsEnabled && <Card className="p-4"><div className="flex items-center justify-between"><span className="text-xs uppercase text-muted-foreground">{t("rep_est_profit")}</span><DollarSign className="w-4 h-4 text-primary" /></div><div className={`mt-2 text-xl font-heading font-bold tabular-nums ${profit?.profit>=0?"text-emerald-600":"text-destructive"}`}>{fmtMoney(profit?.profit, profit?.base_currency || cur)}</div><div className="text-xs text-muted-foreground">{t("rep_revenue_word")} {fmtMoney(profit?.revenue, profit?.base_currency || cur)}</div></Card>}
        <Card className="p-4"><div className="flex items-center justify-between"><span className="text-xs uppercase text-muted-foreground">{t("rep_inv_value")}</span><Package className="w-4 h-4" /></div><div className="mt-2 text-xl font-heading font-bold tabular-nums">{fmtMoney(inv?.total_value, inv?.base_currency || cur)}</div><div className="text-xs text-muted-foreground">{inv?.count||0} {t("rep_item_word")}</div></Card>
      </div>

      <Tabs value={tab} onValueChange={onTabChange}>
        <TabsList>
          <TabsTrigger value="sales" data-testid="tab-sales">{t("rep_tab_sales")}</TabsTrigger>
          <TabsTrigger value="purchases" data-testid="tab-purchases">{t("rep_tab_purchases")}</TabsTrigger>
          {advancedReportsEnabled && <TabsTrigger value="pnl" data-testid="tab-pnl">{t("rep_tab_pnl")}</TabsTrigger>}
          <TabsTrigger value="vat" data-testid="tab-vat">{t("rep_tab_vat")}</TabsTrigger>
          <TabsTrigger value="inventory" data-testid="tab-inventory">{t("rep_tab_inventory")}</TabsTrigger>
          {advancedReportsEnabled && <TabsTrigger value="top" data-testid="tab-top">{t("rep_tab_top")}</TabsTrigger>}
          <TabsTrigger value="alerts" data-testid="tab-alerts">{t("rep_tab_alerts")}</TabsTrigger>
        </TabsList>

        <TabsContent value="sales">
          <div className="grid md:grid-cols-3 gap-3 mb-3" data-testid="sales-multi-currency-summary">
            {(Object.entries(sales?.currency_totals || {})).map(([code, value]) => <Card key={code} className="p-3"><div className="text-xs text-muted-foreground">{ti("rep_sales_of", { c: code })}</div><div className="text-lg font-bold tabular-nums">{fmtMoney(value, code)}</div></Card>)}
            {Object.entries(sales?.totals_by_reporting_currency || {}).map(([code, value]) => <Card key={`report-${code}`} className="p-3 bg-primary/5"><div className="text-xs text-muted-foreground">{ti("rep_report_total_of", { c: code })}</div><div className="text-lg font-bold tabular-nums">{fmtMoney(value, code)}</div></Card>)}
          </div>
          <div className="flex justify-end gap-2 mb-2">
            <Button variant="outline" size="sm" onClick={()=>exportFile("sales","pdf")} data-testid="export-sales-pdf"><FileType className="w-4 h-4 me-1" /> PDF</Button>
            <Button variant="outline" size="sm" onClick={()=>exportFile("sales","excel")} data-testid="export-sales-excel"><Download className="w-4 h-4 me-1" /> Excel</Button>
          </div>
          <SimpleInvoiceTable rows={sales?.items || []} cur={cur} />
        </TabsContent>
        <TabsContent value="purchases">
          <div className="grid md:grid-cols-3 gap-3 mb-3" data-testid="purchases-multi-currency-summary">
            {Object.entries(purchases?.currency_totals || {}).map(([code, value]) => <Card key={`purchase-${code}`} className="p-3"><div className="text-xs text-muted-foreground">{ti("rep_purchases_of", { c: code })}</div><div className="text-lg font-bold tabular-nums">{fmtMoney(value, code)}</div></Card>)}
            {Object.entries(purchases?.totals_by_reporting_currency || {}).map(([code, value]) => <Card key={`purchase-report-${code}`} className="p-3 bg-primary/5"><div className="text-xs text-muted-foreground">{ti("rep_report_total_of", { c: code })}</div><div className="text-lg font-bold tabular-nums">{fmtMoney(value, code)}</div></Card>)}
          </div>
          <div className="flex justify-end gap-2 mb-2">
            <Button variant="outline" size="sm" onClick={()=>exportFile("purchases","pdf")}><FileType className="w-4 h-4 me-1" /> PDF</Button>
            <Button variant="outline" size="sm" onClick={()=>exportFile("purchases","excel")}><Download className="w-4 h-4 me-1" /> Excel</Button>
          </div>
          <SimpleInvoiceTable rows={purchases?.items || []} cur={cur} />
        </TabsContent>
        {advancedReportsEnabled && <TabsContent value="pnl" data-testid="pnl-content">
          <Card className="p-6" data-testid="pnl-card">
            <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
              <div>
                <div className="flex items-center gap-2 text-lg font-heading font-bold"><Wallet className="w-5 h-5 text-primary" /> {t("rep_pnl_title")}</div>
                <p className="text-xs text-muted-foreground mt-1">{t("rep_pnl_formula_desc")}</p>
              </div>
            </div>
            {!pnl && <div className="text-sm text-muted-foreground text-center py-8">{t("rep_press_refresh")}</div>}
            {pnl && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-md" data-testid="pnl-revenue">
                    <div className="text-xs text-emerald-700 font-bold">{t("rep_revenue_sales")}</div>
                    <div className="text-xl font-bold tabular-nums mt-1 text-emerald-800">{fmtMoney(pnl.revenue, cur)}</div>
                    <div className="text-[10px] text-emerald-600 mt-0.5">{pnl.sales_count || 0} {t("rep_invoice_word")}</div>
                  </div>
                  <div className="p-4 bg-red-50 border border-red-200 rounded-md" data-testid="pnl-cogs">
                    <div className="text-xs text-red-700 font-bold">{t("rep_cogs")}</div>
                    <div className="text-xl font-bold tabular-nums mt-1 text-red-800">- {fmtMoney(pnl.cogs, cur)}</div>
                  </div>
                  <div className="p-4 bg-primary/10 border border-primary/30 rounded-md" data-testid="pnl-gross">
                    <div className="text-xs text-primary font-bold">{t("rep_gross")}</div>
                    <div className={`text-xl font-bold tabular-nums mt-1 ${pnl.gross_profit >= 0 ? "text-primary" : "text-destructive"}`}>{fmtMoney(pnl.gross_profit, cur)}</div>
                    <div className="text-[10px] text-muted-foreground mt-0.5">{t("rep_margin")} {pnl.revenue > 0 ? ((pnl.gross_profit / pnl.revenue) * 100).toFixed(1) : 0}%</div>
                  </div>
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-md" data-testid="pnl-expenses">
                    <div className="text-xs text-amber-700 font-bold">{t("rep_opex")}</div>
                    <div className="text-xl font-bold tabular-nums mt-1 text-amber-800">- {fmtMoney(pnl.total_expenses, cur)}</div>
                  </div>
                </div>

                <Card className={`p-6 ${pnl.net_profit >= 0 ? "bg-emerald-600" : "bg-destructive"} text-white`} data-testid="pnl-net">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                      <div className="text-xs opacity-90 font-bold uppercase tracking-wider">{t("rep_net_pl")}</div>
                      <div className="text-4xl font-heading font-bold tabular-nums mt-2">{fmtMoney(pnl.net_profit, cur)}</div>
                      <div className="text-xs opacity-90 mt-2">
                        {pnl.net_profit >= 0
                          ? ti("rep_net_profit_note", { m: pnl.revenue > 0 ? ((pnl.net_profit / pnl.revenue) * 100).toFixed(1) : 0 })
                          : t("rep_net_loss_note")}
                      </div>
                    </div>
                    <Wallet className="w-16 h-16 opacity-30" />
                  </div>
                </Card>

                {pnl.expenses && Object.keys(pnl.expenses).length > 0 && (
                  <div>
                    <div className="text-sm font-bold mb-2">{t("rep_expenses_by_cat")}</div>
                    <div className="border border-border rounded-md bg-card">
                      <Table>
                        <TableHeader><TableRow><TableHead>{t("category")}</TableHead><TableHead>{t("rep_amount")}</TableHead><TableHead>{t("rep_pct_of_revenue")}</TableHead></TableRow></TableHeader>
                        <TableBody>
                          {Object.entries(pnl.expenses).map(([cat, amt]) => (
                            <TableRow key={cat}>
                              <TableCell className="font-semibold">{cat || t("rep_uncategorized")}</TableCell>
                              <TableCell className="tabular-nums text-red-600 font-bold">{fmtMoney(amt, cur)}</TableCell>
                              <TableCell className="tabular-nums text-xs text-muted-foreground">{pnl.revenue > 0 ? ((amt / pnl.revenue) * 100).toFixed(1) : 0}%</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}

                <div className="text-xs text-muted-foreground pt-2 border-t border-border">
                  {ti("rep_pnl_eq", { r: fmtMoney(pnl.revenue, cur), c: fmtMoney(pnl.cogs, cur), e: fmtMoney(pnl.total_expenses, cur), n: fmtMoney(pnl.net_profit, cur) })}
                </div>
              </div>
            )}
          </Card>
        </TabsContent>}
        <TabsContent value="vat" data-testid="vat-content">
          <Card className="p-6" data-testid="vat-card">
            <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
              <div>
                <div className="flex items-center gap-2 text-lg font-heading font-bold"><Receipt className="w-5 h-5 text-primary" /> {t("rep_vat_title")}</div>
                <p className="text-xs text-muted-foreground mt-1">{t("rep_vat_desc")}</p>
                {currentCompany?.vat_number && <p className="text-[11px] text-muted-foreground mt-0.5">{t("rep_vat_number")} <span className="font-semibold text-foreground">{currentCompany.vat_number}</span></p>}
              </div>
              {vat && <Button onClick={printVat} variant="outline" className="h-9" data-testid="vat-print"><FileType className="w-4 h-4 me-1" /> {t("rep_print_declaration")}</Button>}
            </div>
            {!vat && <div className="text-sm text-muted-foreground text-center py-8">{t("rep_press_refresh")}</div>}
            {vat && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-md" data-testid="vat-output">
                    <div className="text-xs text-emerald-700 font-bold">{t("rep_output_vat")}</div>
                    <div className="text-2xl font-bold tabular-nums mt-1 text-emerald-800">{fmtMoney(vat.output_vat, vat.base_currency || cur)}</div>
                    <div className="text-[10px] text-emerald-600 mt-0.5">{ti("rep_taxable_base", { x: fmtMoney(vat.output_taxable, vat.base_currency || cur), n: vat.sale_invoices })}</div>
                  </div>
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-md" data-testid="vat-input">
                    <div className="text-xs text-amber-700 font-bold">{t("rep_input_vat")}</div>
                    <div className="text-2xl font-bold tabular-nums mt-1 text-amber-800">{fmtMoney(vat.input_vat, vat.base_currency || cur)}</div>
                    <div className="text-[10px] text-amber-600 mt-0.5">{ti("rep_taxable_base", { x: fmtMoney(vat.input_taxable, vat.base_currency || cur), n: vat.purchase_invoices })}</div>
                  </div>
                  <div className={`p-4 rounded-md border ${vat.net_vat_payable >= 0 ? "bg-primary/10 border-primary/30" : "bg-emerald-100 border-emerald-300"}`} data-testid="vat-net">
                    <div className="text-xs text-primary font-bold">{t("rep_net_vat")}</div>
                    <div className={`text-2xl font-bold tabular-nums mt-1 ${vat.net_vat_payable >= 0 ? "text-primary" : "text-emerald-700"}`}>{fmtMoney(vat.net_vat_payable, vat.base_currency || cur)}</div>
                    <div className="text-[10px] text-muted-foreground mt-0.5">{vat.net_vat_payable >= 0 ? t("rep_vat_payable") : t("rep_vat_credit")}</div>
                  </div>
                </div>
                <div className="text-xs text-muted-foreground pt-2 border-t border-border">
                  {ti("rep_vat_note", { out: fmtMoney(vat.output_vat, vat.base_currency || cur), in: fmtMoney(vat.input_vat, vat.base_currency || cur), net: fmtMoney(vat.net_vat_payable, vat.base_currency || cur), cur: vat.base_currency || cur })}
                </div>
              </div>
            )}
          </Card>
        </TabsContent>
        <TabsContent value="inventory">
          <div className="flex justify-end gap-2 mb-2">
            <Button variant="outline" size="sm" onClick={()=>exportFile("inventory","pdf")}><FileType className="w-4 h-4 me-1" /> PDF</Button>
            <Button variant="outline" size="sm" onClick={()=>exportFile("inventory","excel")}><Download className="w-4 h-4 me-1" /> Excel</Button>
          </div>
          {Array.isArray(inv?.by_currency) && inv.by_currency.length > 0 && (
            <div className="mb-3 grid md:grid-cols-2 gap-3" data-testid="inventory-by-currency">
              {inv.by_currency.map((c) => (
                <div key={c.currency} className="rounded-md border border-border bg-card px-4 py-3 flex items-center justify-between" data-testid={`inv-currency-${c.currency}`}>
                  <div>
                    <div className="font-bold text-sm">{currencyName(c.currency)} ({c.currency})</div>
                    <div className="text-xs text-muted-foreground">{c.count} {t("rep_item_word")}</div>
                  </div>
                  <div className="text-lg font-bold tabular-nums text-primary">{fmtMoney(c.value, c.currency)}</div>
                </div>
              ))}
              <div className="md:col-span-2 rounded-md bg-primary text-primary-foreground px-4 py-3 flex items-center justify-between">
                <span className="text-sm opacity-90">{t("rep_total_inv_value")}{inv.multi_currency ? ` ${ti("rep_converted_to", { c: inv.base_currency })}` : ""}</span>
                <span className="text-xl font-bold tabular-nums">{fmtMoney(inv.total_value, inv.base_currency || cur)}</span>
              </div>
            </div>
          )}
          <div className="border border-border rounded-md bg-card">
            <Table>
              <TableHeader><TableRow><TableHead>{t("rep_product")}</TableHead><TableHead>{t("barcode")}</TableHead><TableHead>{t("rep_stock")}</TableHead><TableHead>{t("rep_min_stock")}</TableHead><TableHead>{t("rep_value")}</TableHead></TableRow></TableHeader>
              <TableBody>
                {inv?.items?.map(p => <TableRow key={p.id}><TableCell className="font-semibold">{p.name}</TableCell><TableCell className="text-xs font-mono">{p.barcode}</TableCell><TableCell className="tabular-nums">{p.stock}</TableCell><TableCell className="tabular-nums">{p.min_stock}</TableCell><TableCell className="tabular-nums">{fmtMoney(p.stock*p.cost_price, p.currency)}</TableCell></TableRow>)}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
        {advancedReportsEnabled && <TabsContent value="top">
          <div className="flex justify-end gap-2 mb-2">
            <Button variant="outline" size="sm" onClick={()=>exportFile("top-selling","pdf")}><FileType className="w-4 h-4 me-1" /> PDF</Button>
            <Button variant="outline" size="sm" onClick={()=>exportFile("top-selling","excel")}><Download className="w-4 h-4 me-1" /> Excel</Button>
          </div>
          <div className="border border-border rounded-md bg-card">
            <Table>
              <TableHeader><TableRow><TableHead className="w-14">#</TableHead><TableHead>{t("rep_product")}</TableHead><TableHead>{t("rep_qty_sold")}</TableHead><TableHead>{t("rep_revenue")}</TableHead></TableRow></TableHeader>
              <TableBody>
                {top?.items?.map((t2,i) => <TableRow key={t2._id}><TableCell className="font-bold text-primary"><Award className="w-4 h-4 inline me-1" />{i+1}</TableCell><TableCell className="font-semibold">{t2.name}</TableCell><TableCell className="tabular-nums">{t2.qty}</TableCell><TableCell className="tabular-nums font-bold">{fmtMoney(t2.revenue, cur)}</TableCell></TableRow>)}
                {(!top?.items || top.items.length===0) && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-6">{t("rep_no_data")}</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </TabsContent>}
        <TabsContent value="alerts">
          <div className="flex justify-end gap-2 mb-2">
            <Button variant="outline" size="sm" onClick={()=>exportFile("low-stock","pdf")}><FileType className="w-4 h-4 me-1" /> {t("rep_pdf_lowstock")}</Button>
            <Button variant="outline" size="sm" onClick={()=>exportFile("expiring","pdf")}><FileType className="w-4 h-4 me-1" /> {t("rep_pdf_expiring")}</Button>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="border border-border rounded-md bg-card">
              <div className="p-3 border-b border-border font-semibold flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-destructive" /> {ti("rep_below_min", { n: lowStock.length })}</div>
              <Table>
                <TableHeader><TableRow><TableHead>{t("rep_product")}</TableHead><TableHead>{t("rep_stock")}</TableHead><TableHead>{t("rep_limit")}</TableHead></TableRow></TableHeader>
                <TableBody>
                  {lowStock.map(p => <TableRow key={p.id}><TableCell className="font-semibold">{p.name}</TableCell><TableCell className="tabular-nums text-destructive font-bold">{p.stock}</TableCell><TableCell className="tabular-nums">{p.min_stock}</TableCell></TableRow>)}
                  {lowStock.length===0 && <TableRow><TableCell colSpan={3} className="text-center text-muted-foreground py-6">{t("rep_no_alerts")}</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
            <div className="border border-border rounded-md bg-card">
              <div className="p-3 border-b border-border font-semibold flex items-center gap-2"><CalendarClock className="w-4 h-4 text-amber-600" /> {ti("rep_expiring_title", { n: expiring.length })}</div>
              <Table>
                <TableHeader><TableRow><TableHead>{t("rep_product")}</TableHead><TableHead>{t("rep_expiry")}</TableHead><TableHead>{t("rep_batch")}</TableHead></TableRow></TableHeader>
                <TableBody>
                  {expiring.map(p => <TableRow key={p.id}><TableCell className="font-semibold">{p.name}</TableCell><TableCell className="tabular-nums text-amber-700">{p.expiry_date}</TableCell><TableCell className="text-xs">{p.batch_number||"—"}</TableCell></TableRow>)}
                  {expiring.length===0 && <TableRow><TableCell colSpan={3} className="text-center text-muted-foreground py-6">{t("rep_no_alerts")}</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function SimpleInvoiceTable({ rows, cur }) {
  const { t } = useI18n();
  return (
    <div className="border border-border rounded-md bg-card">
      <Table>
        <TableHeader><TableRow><TableHead>{t("rep_number")}</TableHead><TableHead>{t("date")}</TableHead><TableHead>{t("rep_party")}</TableHead><TableHead>{t("type")}</TableHead><TableHead>{t("total")}</TableHead><TableHead>{t("rep_fx")}</TableHead></TableRow></TableHeader>
        <TableBody>
          {rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-6 text-muted-foreground">{t("rep_no_data")}</TableCell></TableRow>}
          {rows.map(i => (
            <TableRow key={i.id}>
              <TableCell>#{i.number}</TableCell>
              <TableCell className="text-xs">{new Date(i.created_at).toLocaleDateString("ar-EG")}</TableCell>
              <TableCell className="font-semibold">{i.party_name}</TableCell>
              <TableCell><span className="text-xs">{i.payment_type==="cash"?t("rep_cash"):t("rep_credit")}</span></TableCell>
              <TableCell className="tabular-nums font-bold">{fmtMoney(i.total, i.currency||cur)}</TableCell>
              <TableCell className="tabular-nums text-xs">{i.exchange_rate || "—"}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
